import { htmlToCalendarText } from '../../../lib/utils/calendarText';
import type { ActivitySyncContext } from './types';

/**
 * Tipi di attività per cui il drawer propone attendees/invitati.
 * Fonte: LeadStatusUpdateDrawer.tsx (tre copie identiche a :627, :1179, :1530, :1649
 * — unificate qui in un'unica costante).
 */
export const TYPES_WITH_ATTENDEES = ['videochiamata', 'appuntamento', 'sopralluogo'] as const;

const TYPES_WITH_ATTENDEES_SET = new Set<string>(TYPES_WITH_ATTENDEES);

/** Payload evento nel formato atteso dalla edge function google-calendar-modify. */
export interface GoogleEventPayload {
  title: string;
  /** undefined = non toccare la description dell'evento (PATCH la ignora). */
  description: string | undefined;
  start_time: string;
  end_time: string;
  all_day: boolean;
  location: string;
  /** Metadata opzionale usata dall'edge function per l'enhancement server-side del titolo/descrizione. */
  client_name?: string;
  crm_url?: string;
}

/**
 * Costruisce il payload evento calendario a partire dal contesto risolto del drawer.
 *
 * Fonte: `createEnhancedEventData` in LeadStatusUpdateDrawer.tsx:341-415, parametrizzata:
 * - il link CRM usa `ctx.entityUrl` (nella fonte era hardcoded `${origin}/leads/${leadId}` a :353);
 * - il gate `ctx.includeDescriptionInEvent` replica ESATTAMENTE la fonte: si applica SOLO al
 *   ramo attendee-types (:375). Il ramo "enhanced" (:392-399) include SEMPRE la descrizione,
 *   senza gate — porting fedele, non un fix di coerenza;
 * - `Telefono`/`Email` del cliente (fonte :394-396) sono ripristinate nel ramo enhanced tramite
 *   i due campi opzionali `ctx.clientPhone`/`ctx.clientEmail` (aggiunti a `ActivitySyncContext`),
 *   con le stesse due righe SEMPRE presenti e fallback `'N/D'` quando il dato manca — fedeltà
 *   1:1 con la fonte (`primaryPhone`/`primaryEmail` a :386-387, sempre con fallback `'N/D'`);
 * - il link CRM (riga finale) è presente solo se `ctx.entityUrl` è valorizzato — nella fonte
 *   `crmUrl` era sempre calcolato (mai null), qui il contratto lo rende opzionale;
 * - `client_id` è stato rimosso: non presente in `ActivitySyncContext`, ed era usato lato
 *   edge function solo per logging di debug (non per logica).
 *
 * Per i tipi in TYPES_WITH_ATTENDEES: evento semplificato senza dettagli CRM (comportamento
 * storico, per non esporre dati cliente nell'invito calendario condiviso con gli attendees).
 * Per gli altri tipi: evento "enhanced" con nome cliente in titolo e dettagli cliente/CRM in descrizione.
 */
export function createEventData(ctx: ActivitySyncContext): GoogleEventPayload {
  const startTime = ctx.startDateTime.toISOString();
  const endTime = ctx.endDateTime
    ? ctx.endDateTime.toISOString()
    : new Date(ctx.startDateTime.getTime() + 60 * 60 * 1000).toISOString();

  if (TYPES_WITH_ATTENDEES_SET.has(ctx.type)) {
    // Spunta spenta: '' = rimozione esplicita (stato noto), undefined = non toccare
    // (attività storica senza flag persistito — bug note-cancellate 31/08/2026).
    const offDescription = ctx.includeDescriptionKnown === false ? undefined : '';
    return {
      title: ctx.title,
      description: ctx.includeDescriptionInEvent ? htmlToCalendarText(ctx.descriptionHtml) : offDescription,
      start_time: startTime,
      end_time: endTime,
      all_day: false,
      location: '',
    };
  }

  const clientName = ctx.clientName || 'Cliente';

  // Ramo enhanced: la descrizione NON è gated da includeDescriptionInEvent (fedele a :392-399).
  const descriptionLines: string[] = [htmlToCalendarText(ctx.descriptionHtml)];
  descriptionLines.push(`Cliente: ${clientName}`);
  descriptionLines.push(`Telefono: ${ctx.clientPhone || 'N/D'}`);
  descriptionLines.push(`Email: ${ctx.clientEmail || 'N/D'}`);
  if (ctx.entityUrl) {
    descriptionLines.push('', `🔗 Apri nel CRM: ${ctx.entityUrl}`);
  }

  return {
    title: `${ctx.title} - ${clientName}`,
    description: descriptionLines.join('\n'),
    start_time: startTime,
    end_time: endTime,
    all_day: false,
    location: '',
    client_name: clientName,
    crm_url: ctx.entityUrl || undefined,
  };
}

/**
 * Deduplica gli attendees di un evento calendario, SOLO per i tipi in TYPES_WITH_ATTENDEES.
 * Per gli altri tipi ritorna sempre array vuoto (nessun invito).
 *
 * Funzione pura: riceve email già risolte dal chiamante (le query ai profili utente/
 * commerciale restano fuori da questo modulo).
 *
 * Fonte: unifica TRE copie della fonte in LeadStatusUpdateDrawer.tsx che, a dispetto della
 * somiglianza, applicano policy DIVERSE (quali fonti concorrono all'elenco attendees):
 * - update (:1178-1211): selected + currentUser + assignee — SENZA invitedCommercial;
 * - insert Google (:1548-1596): selected + currentUser + assignee + invitedCommercial (tutte e 4);
 * - insert Microsoft (:1648-1655): SOLO selected + currentUser — SENZA assignee né invitedCommercial.
 *
 * Questa funzione è un superset parametrico delle tre: include automaticamente ogni campo che
 * il chiamante valorizza. Per restare fedele alla policy del proprio branch, il chiamante DEVE
 * passare `null` (o omettere/non risolvere) i campi che quella policy non prevede — es. il ramo
 * "update" passa `invitedCommercialEmail: null`; il ramo "insert Microsoft" passa
 * `assigneeEmail: null` e `invitedCommercialEmail: null`. Nessuna delle tre copie sorgente
 * deduplicava con l'email del commerciale invitato se non prevista dalla propria policy: passare
 * un valore non-null quando la policy non lo prevede introdurrebbe una regressione.
 */
export function buildAttendees(input: {
  type: string;
  selected: string[];
  currentUserEmail: string | null;
  assigneeEmail: string | null;
  invitedCommercialEmail: string | null;
}): string[] {
  if (!TYPES_WITH_ATTENDEES_SET.has(input.type)) return [];

  const all = [...input.selected, input.currentUserEmail, input.assigneeEmail, input.invitedCommercialEmail].filter(
    (email): email is string => !!email
  );

  return [...new Set(all)];
}
